<template>
  <div>
    <GlobalEvents
      v-if="user"
      @keyup="keepAlive"
      @mousemove="keepAlive"
    />
    <sweet-modal ref="modal"
                 title="Session Timeout"
                 :blocking="true"
                 overlay-theme="dark"
                 :hide-close-button="true"
                 @open="focusPassword"
                 @close="close"
    >
      <div>Your session has timed out. Please provide your password to login again.</div>

      <div class="text-danger mt-4" v-show="loginErrors">
        <div v-for="error in loginErrors" :key="error.code">{{ error.code | parseError }}</div>
      </div>

      <form v-if="isOpen">
        <div class="form-group mt-3">
          <div class="input-group">
            <input class="form-control" :value="email" disabled="disabled"/>
            <div class="input-group-append">
              <span class="input-group-text"><i class="fas fa-user fa-fw"/></span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <div class="input-group">
            <input
              type="password"
              ref="password"
              class="form-control"
              placeholder="Password"
              v-model="password"
              :disabled="loggingIn"
              @keyup.enter="login"
            />
            <div class="input-group-append">
              <span class="input-group-text"><i class="fas fa-lock fa-fw"/></span>
            </div>
          </div>
        </div>
      </form>

      <div class="text-right">
        <a href="javascript:;" @click="differentUser">Login with a different user</a>
      </div>

      <SisenseLogout></SisenseLogout>

      <template slot="button">
        <button
          class="btn btn-outline-primary"
          :disabled="!hasPassword || loggingIn"
          @click="login"
        >
          <i class="fas" :class="[loginIcon]"/>
          Login
        </button>
      </template>
    </sweet-modal>
  </div>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue'
import { mapState, mapMutations } from 'vuex'
import _ from 'lodash'
import loginService from './login-service'
import exceptionHandler from '@/common/exception-handler'
import GlobalEvents from 'vue-global-events'
import exceptionMixin from '@/common/mixins/exception-mixin'
import SisenseLogout from '@/common/elements/SisenseLogout'

export default {
  name: 'LoginPrompt',
  mixins: [exceptionMixin],
  components: {
    SweetModal,
    SisenseLogout,
    GlobalEvents
  },
  data () {
    return {
      password: '',
      loggingIn: false,
      loginErrors: [],
      isOpen: false
    }
  },
  computed: {
    ...mapState('login', ['lastKeepAlive', 'keepingAlive', 'user', 'loginPrompt']),
    email () {
      return _.get(this.user, 'userEmail')
    },
    loginIcon () {
      return this.loggingIn ? 'fa-sync-alt fa-spin' : 'fa-key'
    },
    hasPassword () {
      return this.password.length > 0
    }
  },
  watch: {
    loginPrompt (prompt) {
      if (prompt) {
        this.isOpen = true
        this.$refs.modal.open()
      }
    }
  },
  methods: {
    ...mapMutations('login', ['SET_LOGIN_PROMPT']),
    focusPassword () {
      this.$nextTick(() => {
        this.$refs.password.focus()
      })
    },
    differentUser () {
      this.$refs.modal.close()
      this.$store.commit('login/SET_USER', null)
      this.$router.push({ name: 'login' }).catch(() => {})
    },
    close () {
      this.password = ''
      this.isOpen = false
      this.SET_LOGIN_PROMPT(false)
    },
    async login () {
      if (_.isEmpty(this.password)) {
        return
      }

      this.loggingIn = true
      try {
        const user = await loginService.login(this.email, this.password, this.user.userCompanyId)
        this.$store.commit('login/SET_USER', user)
        this.$refs.modal.close()
      } catch (error) {
        if (exceptionHandler.isUserError(error)) {
          this.loginErrors = error
        } else {
          this.handleError(error, 'Unable to Login, please try again later.')
        }
      } finally {
        this.loggingIn = false
      }
    },
    keepAlive: _.throttle(function () {
      if (this.keepingAlive || this.loginPrompt || !this.user) {
        return
      }

      if (!this.lastKeepAlive || this.minutesElapsed(this.lastKeepAlive) >= 55) {
        this.$store.dispatch('login/keepAlive')
      }
    }, 10000),
    minutesElapsed (start) {
      return (Date.now() - start) / 1000 / 60
    }
  }
}
</script>
